import React, { useCallback, useMemo } from 'react';
import {
  FileInfo,
  FileMultipleInput
} from 'components/common/File/FileMultipleInput';
import styles from './multiFileInputField.module.scss';
import classnames from 'classnames/bind';
import { useField, useFormikContext } from 'formik';
import { default as ReactTags } from 'components/common/Tags/Tags';
import { FieldLayoutProps, withFieldLayout } from 'hoc/withFieldLayout';
import { FormikFieldProps } from 'hoc/withFormikField';
import { defaultTo, get } from 'lodash';

const cx = classnames.bind(styles);

interface MultiFileInputProps {
  validTypes?: string[];
  className?: string;
  name: string;
  disabled?: boolean;
  files?: FileInfo[];
  onChange: (fileData: FileInfo[] | undefined) => void;
}

export interface FormikMultiFileInputProps
  extends Omit<MultiFileInputProps, 'onChange'>, FormikFieldProps {}
export interface FormikMultiFileInputWithFieldLayoutProps
  extends
    Omit<MultiFileInputProps, 'onChange'>,
    Omit<FieldLayoutProps, 'hint' | 'name'>,
    FormikFieldProps {
  hints?: string[];
}
export type FormikMultiFileInputFieldProps =
  | ({ withFieldLayout?: true } & FormikMultiFileInputWithFieldLayoutProps)
  | ({ withFieldLayout: false } & FormikMultiFileInputProps);

const MultiFileDetail = ({
  onChange,
  files
}: {
  onChange: (files: FileInfo[] | undefined) => void;
  files?: FileInfo[];
}) => {
  const onTagsChange = useCallback(
    (options) => {
      files
        ? onChange(
            options.length === 0
              ? undefined
              : options.map((option) =>
                  files.find((file) => file.name === option.value)
                )
          )
        : onChange(undefined);
    },
    [files, onChange]
  );

  if (!files) {
    return <div />;
  }

  // Tags renders `{ label, value }` option objects at runtime even though its
  // prop type is declared as string[] (see the image variant).
  const value = files.map((file) => ({
    label: file.name,
    value: file.name
  })) as any;
  return <ReactTags value={value} disableInput onChange={onTagsChange} />;
};

const MultiFileInputContent = ({
  files,
  className,
  ...props
}: MultiFileInputProps) => {
  return (
    <div className={styles.fileFormInputWithList}>
      <div className={className}>
        <FileMultipleInput files={files} {...props} />
      </div>
      {files && files.length > 0 && (
        <div className={styles.selectFileList}>
          <MultiFileDetail files={files} onChange={props.onChange} />
        </div>
      )}
    </div>
  );
};

const MemorizedMultiFileInputContent = React.memo(MultiFileInputContent);

const MultiFileInputWithFieldLayout = withFieldLayout<
  MultiFileInputProps & Omit<FieldLayoutProps, 'hint' | 'name'>
>(MultiFileInputContent);

const FormikMultiFileInput: React.FC<FormikMultiFileInputProps> = ({
  name,
  validate,
  ...props
}) => {
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();
  const [{ value }] = useField({ name, validate });

  const handleChange = useCallback(
    (newValue) => {
      setFieldValue(name, newValue);
      setFieldTouched(name, true);
    },
    [name, setFieldValue, setFieldTouched]
  );

  const inputClass = cx('fileInput', {
    emptyFileInput: !value,
    previewMultipleInput: value && value.length > 1,
    showSolidBorder: !!value
  });

  return (
    <MemorizedMultiFileInputContent
      {...props}
      name={name}
      files={value}
      onChange={handleChange}
      className={inputClass}
    />
  );
};

const FormikMultiFileInputWithFieldLayout: React.FC<
  FormikMultiFileInputWithFieldLayoutProps
> = ({ name, validate, hints, className: _className, ...props }) => {
  const { setFieldValue, setFieldTouched, submitCount } =
    useFormikContext<any>();
  const [{ value }, { error, touched: formikTouched }] = useField({
    name,
    validate
  });
  const touched = defaultTo(submitCount, 0) > 0 || formikTouched;

  const hint = useMemo(() => {
    const hintElements = hints
      ? hints.map((h, index) => {
          if (index === hints.length - 1) {
            return h;
          }
          return (
            <span key={index}>
              {h}
              <br />
            </span>
          );
        })
      : '';
    return <>{hintElements}</>;
  }, [hints]);

  const handleChange = useCallback(
    (newValue) => {
      setFieldValue(name, newValue);
      setFieldTouched(name, true);
    },
    [name, setFieldValue, setFieldTouched]
  );

  const hasError = error !== undefined && touched;
  const inputClass = cx('fileInput', {
    emptyImageInput: !value,
    previewMultipleInput: value && value.length > 1,
    showErrorBorder: hasError,
    showSolidBorder: !!value
  });

  return (
    <MultiFileInputWithFieldLayout
      {...props}
      name={name}
      files={value}
      onChange={handleChange}
      className={inputClass}
      error={hasError ? error : undefined}
      hint={hasError ? undefined : hint}
      fieldContentWidth='auto'
    />
  );
};

export const FormikMultiFileInputField: React.FC<
  FormikMultiFileInputFieldProps
> = ({ withFieldLayout, ...props }) => {
  if (withFieldLayout === false) {
    return <FormikMultiFileInput {...props} />;
  }
  const hints = get(props, 'hints', []);
  return <FormikMultiFileInputWithFieldLayout {...props} hints={hints} />;
};
